import { memo, useCallback, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import Dropdown from '../Dropdown.jsx';
import {
  exportRichTextToDocx,
  exportRichTextToPdf,
} from './richTextEditorExport.js';

const DEFAULT_TRIGGER = 'Download';
const DEFAULT_PDF = 'PDF';
const DEFAULT_DOCX = 'DOCX';
const DEFAULT_DOWNLOAD_ARIA = 'Download';

/** Stable class string for Dropdown (avoid new string identity each toolbar render). */
const DOWNLOAD_MENU_PANEL_CLASS = 'scrollbar-aimsos-auto-sm min-w-[10rem]';

/**
 * Padding / ancho mínimo solo para el trigger Download del RichTextEditor (p. ej. label vacío = solo icono).
 * No se aplica a otros `Dropdown` del paquete.
 */
const DOWNLOAD_TRIGGER_BUTTON_CLASS = '!px-3.5 min-w-9';

/**
 * Toolbar control: Download split button with PDF / DOCX entries.
 * Export runs inside the package (dynamic import of docx / jspdf).
 *
 * Default `downloadTriggerLabel` is **Download**. Use an empty string to show **icon only**
 * (`downloadTriggerAriaLabel` is used for `aria-label` / `title`). Trigger height matches toolbar (`min-h-8`).
 */
function RichTextEditorDownloadMenuInner({
  editor,
  disabled = false,
  downloadTriggerLabel = DEFAULT_TRIGGER,
  downloadTriggerAriaLabel = DEFAULT_DOWNLOAD_ARIA,
  downloadPdfLabel = DEFAULT_PDF,
  downloadDocxLabel = DEFAULT_DOCX,
  downloadFileNameBase = 'document',
  onExportStart,
  onExportSuccess,
  onExportError,
}) {
  const [busy, setBusy] = useState(false);
  /** Avoid listing `busy` in `useCallback` deps (would recreate the handler every export). */
  const busyRef = useRef(false);

  const runExport = useCallback(
    async (format) => {
      if (!editor || disabled || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      onExportStart?.({ format });
      try {
        if (format === 'pdf') {
          await exportRichTextToPdf(editor, downloadFileNameBase);
        } else {
          await exportRichTextToDocx(editor, downloadFileNameBase);
        }
        onExportSuccess?.({ format });
      } catch (error) {
        onExportError?.({ format, error });
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [editor, disabled, downloadFileNameBase, onExportStart, onExportSuccess, onExportError]
  );

  const onPdf = useCallback(() => {
    void runExport('pdf');
  }, [runExport]);

  const onDocx = useCallback(() => {
    void runExport('docx');
  }, [runExport]);

  return (
    <Dropdown
      variant="secondary"
      size="sm"
      disabled={disabled || busy}
      label={downloadTriggerLabel}
      triggerIcon={Download}
      triggerAriaLabel={downloadTriggerAriaLabel}
      triggerButtonCustomHeight="min-h-8 h-8"
      triggerButtonClassName={DOWNLOAD_TRIGGER_BUTTON_CLASS}
      iconPosition="left"
      menuPosition="bottom"
      alignMenu="right"
      usePortal
      menuClassName={DOWNLOAD_MENU_PANEL_CLASS}
    >
      <Dropdown.Item onClick={onPdf}>{downloadPdfLabel}</Dropdown.Item>
      <Dropdown.Item onClick={onDocx}>{downloadDocxLabel}</Dropdown.Item>
    </Dropdown>
  );
}

const RichTextEditorDownloadMenu = memo(RichTextEditorDownloadMenuInner);
RichTextEditorDownloadMenu.displayName = 'RichTextEditorDownloadMenu';

export default RichTextEditorDownloadMenu;
